import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useIsAuthenticated, useUser, useLogout, useAuthLoading } from './stores/authStore'
import { Layout } from './components/layout/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import { Toaster } from './components/ui/toaster'
import { Loading } from './components/ui'
import { ErrorBoundary } from './components/ErrorBoundary'
import { DevBanner } from './components/DevBanner'
import { AWSAccountsProvider } from './hooks/useAWSAccounts'
// Lazy load all major page components for code splitting
const Login = React.lazy(() => import('./pages/Login'))
const Dashboard = React.lazy(() => import('./pages/Dashboard'))
const Repositories = React.lazy(() => import('./pages/Repositories'))
const RepositoryDetail = React.lazy(() => import('./pages/RepositoryDetail'))
const ImageDetail = React.lazy(() => import('./pages/ImageDetail'))
const Deployments = React.lazy(() => import('./pages/Deployments'))
const DeploymentDetail = React.lazy(() => import('./pages/DeploymentDetail'))
const Users = React.lazy(() => import('./pages/Users'))
const Activity = React.lazy(() => import('./pages/Activity'))
const Profile = React.lazy(() => import('./pages/Profile'))
const Settings = React.lazy(() => import('./pages/Settings'))
const NotFound = React.lazy(() => import('./pages/NotFound'))
import { logger } from './services/logger'

// Create a query client - exported for use in other parts of the app
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
      // Ensure fresh data on mount and when auth state changes
      refetchOnMount: 'always',
      // Don't use cache when network is recovered
      refetchOnReconnect: 'always',
    },
    mutations: {
      retry: false, // Don't retry mutations by default
    },
  },
})

// Protected Routes Component
function ProtectedRoutes() {
  const user = useUser()

  return (
    <Routes>
      {/* Admin-only routes */}
      {user?.role === 'Admin' && (
        <>
          <Route
            path="/dashboard"
            element={
              <React.Suspense fallback={<Loading />}>
                <Dashboard />
              </React.Suspense>
            }
          />
          <Route
            path="/users"
            element={
              <React.Suspense fallback={<Loading />}>
                <Users />
              </React.Suspense>
            }
          />
        </>
      )}

      {/* Common routes for all authenticated users */}
      <Route
        path="/repositories"
        element={
          <React.Suspense fallback={<Loading />}>
            <Repositories />
          </React.Suspense>
        }
      />
      <Route
        path="/repositories/:repoName"
        element={
          <React.Suspense fallback={<Loading />}>
            <RepositoryDetail />
          </React.Suspense>
        }
      />
      <Route
        path="/repositories/:repoName/images/:imageName"
        element={
          <React.Suspense fallback={<Loading />}>
            <ImageDetail />
          </React.Suspense>
        }
      />
      <Route
        path="/deployments"
        element={
          <React.Suspense fallback={<Loading />}>
            <Deployments />
          </React.Suspense>
        }
      />
      <Route
        path="/deployments/:id"
        element={
          <React.Suspense fallback={<Loading />}>
            <DeploymentDetail />
          </React.Suspense>
        }
      />
      <Route
        path="/activity"
        element={
          <React.Suspense fallback={<Loading />}>
            <Activity />
          </React.Suspense>
        }
      />
      <Route
        path="/profile"
        element={
          <React.Suspense fallback={<Loading />}>
            <Profile />
          </React.Suspense>
        }
      />
      <Route
        path="/settings"
        element={
          <React.Suspense fallback={<Loading />}>
            <Settings />
          </React.Suspense>
        }
      />

      {/* Redirect to appropriate home page */}
      <Route
        path="/"
        element={<Navigate to={user?.role === 'Admin' ? '/dashboard' : '/repositories'} replace />}
      />

      {/* 404 for authenticated users */}
      <Route
        path="*"
        element={
          <React.Suspense fallback={<Loading />}>
            <NotFound />
          </React.Suspense>
        }
      />
    </Routes>
  )
}

function App() {
  const isAuthenticated = useIsAuthenticated()
  const user = useUser()
  const logout = useLogout()
  const authLoading = useAuthLoading()

  // Initialize logging
  React.useEffect(() => {
    logger.info('Application started', {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    })
  }, [])

  // Show loading screen while auth is being determined
  if (authLoading) {
    return <Loading fullScreen />
  }

  return (
    <AWSAccountsProvider>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <Router>
            <div className="min-h-screen bg-background">
              <Routes>
                {/* Public Routes */}
                <Route
                  path="/login"
                  element={
                    isAuthenticated && user ? (
                      <Navigate
                        to={user.role === 'Admin' ? '/dashboard' : '/repositories'}
                        replace
                      />
                    ) : (
                      <React.Suspense fallback={<Loading />}>
                        <Login />
                      </React.Suspense>
                    )
                  }
                />

                {/* Protected Routes */}
                <Route
                  path="/*"
                  element={
                    isAuthenticated && user ? (
                      <Layout
                        user={{
                          name: user.name,
                          email: user.email,
                          avatar: user.profilePicture,
                          role: user.role.toLowerCase() as 'admin' | 'user',
                        }}
                        onLogout={logout}
                      >
                        <ProtectedRoutes />
                      </Layout>
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
              </Routes>
            </div>
            <Toaster />
          </Router>

          {/* Development Tools */}
          {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
        </ErrorBoundary>
      </QueryClientProvider>
    </AWSAccountsProvider>
  )
}

export default App
